import React, { useId, useMemo } from 'react';
import { useBrand } from '../context/BrandContext';
import { AGENTS, normalizeAgentId } from '../utils/agentConfig';

function normalizeHex(hex, fallback) {
  const raw = String(hex || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return fallback;
}

function hexToRgb(hex) {
  const h = normalizeHex(hex, '#2A9BB5').slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function tint(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const t = Math.min(1, Math.max(0, amount));
  const nr = Math.round(r + (255 - r) * t);
  const ng = Math.round(g + (255 - g) * t);
  const nb = Math.round(b + (255 - b) * t);
  return `#${[nr, ng, nb].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * World-Class Tailored SVG Agent Mark for SmartTTClub
 * Dynamically shifts appearance, geometry, and aura based on agent motivation:
 * - Pingwi: Playful warmth & sparkling discovery
 * - Pingflo: Harmonic flow wave & active energy
 * - Pingpro: High-precision tactical geometry & laser focus
 * - Pingmynd: Zen-like balance rings & cognitive longevity
 *
 * @param {{
 *   agentId?: 'pingwi' | 'pingflo' | 'pingpro' | 'pingmynd' | string,
 *   size?: number,
 *   className?: string,
 *   primary?: string,
 *   accent?: string,
 *   showGlow?: boolean,
 *   title?: string,
 * }} props
 */
export default function AgentMark({
  agentId = 'pingflo',
  size = 40,
  className = '',
  primary,
  accent,
  showGlow = false,
  title,
}) {
  const uid = useId().replace(/:/g, '');
  const { brand } = useBrand();
  const normalizedId = normalizeAgentId(agentId) || 'pingflo';
  const agentMeta = AGENTS[normalizedId] || AGENTS.pingflo;

  const colors = useMemo(() => {
    const primaryColor = normalizeHex(
      primary || agentMeta.primaryColor || brand?.color_primary,
      '#2A9BB5'
    );
    const accentColor = normalizeHex(
      accent || agentMeta.accentColor || brand?.color_primary_light || brand?.color_secondary,
      '#00D2FF'
    );
    const midColor = tint(primaryColor, 0.3);

    return {
      primary: primaryColor,
      accent: accentColor,
      mid: midColor,
      coreTop: tint(accentColor, 0.72),
      coreBottom: tint(primaryColor, 0.32),
      discFill: rgba(primaryColor, 0.18),
      discStroke: rgba(accentColor, 0.35),
      pupil: rgba('#0b0f19', 0.4),
      glow: agentMeta.glowColor || rgba(accentColor, 0.35),
    };
  }, [accent, agentMeta, brand?.color_primary, brand?.color_primary_light, brand?.color_secondary, primary]);

  const ringId = `amRing-${normalizedId}-${uid}`;
  const coreId = `amCore-${normalizedId}-${uid}`;
  const glowFilterId = `amGlow-${normalizedId}-${uid}`;

  return (
    <svg
      className={`agent-mark agent-mark--${normalizedId} ${className}`.trim()}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title || agentMeta.name}
      style={showGlow ? { filter: `drop-shadow(0 4px 12px ${colors.glow})` } : undefined}
    >
      <defs>
        <linearGradient id={ringId} x1="8" y1="4" x2="56" y2="60" gradientUnits="userSpaceOnUse">
          <stop stopColor={colors.accent} />
          <stop offset="0.55" stopColor={colors.mid} />
          <stop offset="1" stopColor={colors.primary} />
        </linearGradient>

        <linearGradient id={coreId} x1="20" y1="16" x2="44" y2="52" gradientUnits="userSpaceOnUse">
          <stop stopColor={colors.coreTop} />
          <stop offset="1" stopColor={colors.coreBottom} />
        </linearGradient>

        {showGlow && (
          <filter id={glowFilterId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        )}
      </defs>

      {/* Base Outer Orbit Ring */}
      <circle
        cx="32"
        cy="32"
        r="29"
        stroke={`url(#${ringId})`}
        strokeWidth="2.4"
        opacity="0.32"
      />

      {/* Dynamic Segment Orbit according to Agent Archetype */}
      {normalizedId === 'pingwi' && (
        <>
          {/* Playful Dotted Orbit & Sparkle arcs */}
          <circle
            cx="32"
            cy="32"
            r="29"
            stroke={`url(#${ringId})`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="16 12 8 12"
            strokeDashoffset="6"
          />
          {/* Inner Joyful Sparkle */}
          <path
            d="M32 10l1.2 3.8 3.8 1.2-3.8 1.2-1.2 3.8-1.2-3.8-3.8-1.2 3.8-1.2z"
            fill={colors.accent}
            opacity="0.9"
          />
        </>
      )}

      {normalizedId === 'pingflo' && (
        <>
          {/* Harmonic Wave Flow Orbit */}
          <circle
            cx="32"
            cy="32"
            r="29"
            stroke={`url(#${ringId})`}
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeDasharray="110 50"
            strokeDashoffset="24"
          />
          {/* Dynamic Flow Rhythm Arc */}
          <path
            d="M12 32c4-8 12-10 20-5s12 11 20 5"
            stroke={colors.accent}
            strokeWidth="1.8"
            strokeLinecap="round"
            fill="none"
            opacity="0.65"
          />
        </>
      )}

      {normalizedId === 'pingpro' && (
        <>
          {/* High-Precision Angular Laser Orbit */}
          <circle
            cx="32"
            cy="32"
            r="29"
            stroke={`url(#${ringId})`}
            strokeWidth="3.2"
            strokeLinecap="square"
            strokeDasharray="140 30"
            strokeDashoffset="10"
          />
          {/* Tactical Target Ticks */}
          <line x1="32" y1="3" x2="32" y2="7" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round" />
          <line x1="61" y1="32" x2="57" y2="32" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round" />
          <line x1="32" y1="61" x2="32" y2="57" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round" />
          <line x1="3" y1="32" x2="7" y2="32" stroke={colors.accent} strokeWidth="2.5" strokeLinecap="round" />
        </>
      )}

      {normalizedId === 'pingmynd' && (
        <>
          {/* Dual Harmonious Balance Rings (Cognitive / Longevity) */}
          <circle
            cx="32"
            cy="32"
            r="29"
            stroke={`url(#${ringId})`}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeDasharray="75 35 75 35"
            strokeDashoffset="12"
          />
          {/* Balance concentric harmony ring */}
          <circle
            cx="32"
            cy="32"
            r="25.5"
            stroke={colors.accent}
            strokeWidth="1.2"
            strokeDasharray="4 6"
            opacity="0.6"
          />
        </>
      )}

      {/* Semi-translucent Glass Central Disc */}
      <circle
        cx="32"
        cy="32"
        r="22"
        fill={colors.discFill}
        stroke={colors.discStroke}
        strokeWidth="1.2"
      />

      {/* Central Athlete / Companion Silhouette Avatar Core */}
      <circle cx="32" cy="24" r="6.5" fill={`url(#${coreId})`} />
      <path
        d="M18 46c2.2-8.5 7.4-12.5 14-12.5S43.8 37.5 46 46"
        stroke={`url(#${coreId})`}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />

      {/* Satellite Table Tennis Ball (Position & Velocity customized by Archetype) */}
      {normalizedId === 'pingpro' ? (
        <>
          {/* High velocity motion trail */}
          <path
            d="M38 14c4 1 8 3 10 5"
            stroke={colors.accent}
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.65"
          />
          <circle cx="49" cy="19" r="4.5" fill={colors.accent} />
          <circle cx="49" cy="19" r="1.8" fill={colors.pupil} />
        </>
      ) : normalizedId === 'pingwi' ? (
        <>
          {/* Bouncing joy satellite */}
          <circle cx="47" cy="17" r="4.8" fill={colors.accent} />
          <circle cx="47" cy="17" r="1.9" fill="#FFF7ED" />
          <circle cx="47" cy="17" r="1.1" fill={colors.pupil} />
        </>
      ) : normalizedId === 'pingmynd' ? (
        <>
          {/* Serene balance satellite */}
          <circle cx="48" cy="18" r="4.2" fill={colors.accent} />
          <circle cx="48" cy="18" r="1.6" fill={colors.pupil} />
          <circle cx="16" cy="46" r="2.8" fill={colors.accent} opacity="0.65" />
        </>
      ) : (
        <>
          {/* Default Pingflo satellite */}
          <circle cx="48" cy="18" r="4.4" fill={colors.accent} />
          <circle cx="48" cy="18" r="1.7" fill={colors.pupil} />
        </>
      )}

      {/* Subtle depth flare arc */}
      <path
        d="M22 30c-3.5 1.2-6 4.2-7 8"
        stroke={colors.accent}
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  );
}
